// ── recipePrint.js — 주방용 한중 병기 레시피 인쇄 (V109) ─────────────────────
//
// 허페이 매장은 VPN이 끊기면 앱이 아예 안 열린다. 그날도 주방은 돌아가야 하므로
// 레시피를 종이로 벽에 붙여둔다 — 이게 접속 장애의 마지막 백업이다.
//
// 원칙:
// - 원가·판매가·이익은 찍지 않는다. 주방 벽에 붙는 종이라 직원 모두가 본다.
// - 한국어가 위, 중국어가 아래. 사장님과 직원이 같은 종이를 보고 말이 통해야 한다.
// - 재료 중국어는 단가표 name_zh(zhMap)에서, 메뉴명·메모 중국어는 레시피의
//   name_zh / note_zh에서 가져온다. 없으면 한국어만 찍힌다 (인쇄는 절대 안 막힘).
// - 레시피 1개든 전체든 같은 함수. 전체 인쇄는 레시피마다 페이지를 나눈다.

import { zhOf } from '../i18n/BiName';
import { pronText, ensurePinyin, isPinyinReady } from '../i18n/pron';   // V127 발음
import { specToZh, unitToZh, hasHangul } from '../i18n/units';   // V154 단가표 인쇄
import { lossG, usableG } from './priceYield';

const esc = (s) => String(s == null ? '' : s)
  .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;').replace(/'/g, '&#39;');

const nl2br = (s) => esc(s).replace(/\r?\n/g, '<br>');

// 12.5 → "12.5", 12 → "12", 3.333 → "3.3"
function fmtQty(n) {
  const v = Number(n) || 0;
  if (v === 0) return '0';
  if (Math.abs(v - Math.round(v)) < 0.05) return String(Math.round(v));
  return v >= 10 ? v.toFixed(0) : v.toFixed(1).replace(/\.0$/, '');
}

const TYPE_LABEL = {
  menu: ['메뉴', '菜品'],
  semi: ['반제품', '半成品'],
};

/**
 * 레시피 1건의 인쇄용 HTML 조각
 * @param {object} r        레시피 (name, name_zh, note, note_zh, ingredients, serving_g, total_output, type)
 * @param {Map} zhMap       단가표 기반 한→중 맵
 * @param {number} servings 몇 인분 기준으로 찍을지 (기본 1)
 */
function recipeBlock(r, zhMap, servings, lang) {
  const ings = Array.isArray(r?.ingredients) ? r.ingredients : [];
  const sv = Math.max(1, Number(servings) || 1);
  // RecipeView.calcRatio와 같은 식 — 레시피 수량은 '총 산출량' 기준이다
  const ratio = (r.serving_g > 0 && r.total_output > 0)
    ? (r.serving_g * sv) / r.total_output
    : sv;
  const showScaled = Math.abs(ratio - 1) > 0.001;

  const t = TYPE_LABEL[r.type] || [r.type || '', ''];
  const nameZh = String(r.name_zh || '').trim();
  const note = String(r.note || '').trim();
  const noteZh = String(r.note_zh || '').trim();

  const rows = ings.map((ing, i) => {
    const unit = ing.unit || 'g';
    const zh = zhOf(zhMap, ing.name);
    // V127: 발음 — 한국어 화면은 중국어 밑에 병음, 중국어 화면은 한국어 밑에 로마자
    const pr = pronText(ing.name, zh, lang);
    const prDiv = pr ? `<div class="py">${esc(pr)}</div>` : '';
    return `<tr>
      <td class="no">${i + 1}</td>
      <td class="nm"><div class="ko">${esc(ing.name)}</div>${lang === 'zh' ? prDiv : ''}${zh ? `<div class="zh">${esc(zh)}</div>` : ''}${lang === 'zh' ? '' : prDiv}</td>
      <td class="q">${esc(fmtQty(ing.qty))}<span class="u">${esc(unit)}</span></td>
      ${showScaled ? `<td class="q strong">${esc(fmtQty((Number(ing.qty) || 0) * ratio))}<span class="u">${esc(unit)}</span></td>` : ''}
    </tr>`;
  }).join('');

  const meta = [];
  if (r.total_output > 0) meta.push(`총 산출량 总产出 <b>${esc(fmtQty(r.total_output))}g</b>`);
  if (r.serving_g > 0) meta.push(`1인분 1人份 <b>${esc(fmtQty(r.serving_g))}g</b>`);

  return `<section class="recipe">
    <header>
      <div class="badge">${esc(t[0])}${t[1] ? ` · ${esc(t[1])}` : ''}</div>
      <h1>${esc(r.name)}</h1>
      ${lang === 'zh' && pronText(r.name, nameZh, lang) ? `<div class="py t">${esc(pronText(r.name, nameZh, lang))}</div>` : ''}
      ${nameZh ? `<h2>${esc(nameZh)}</h2>` : ''}
      ${lang !== 'zh' && pronText(r.name, nameZh, lang) ? `<div class="py t">${esc(pronText(r.name, nameZh, lang))}</div>` : ''}
      ${meta.length ? `<div class="meta">${meta.join('<span class="sep">|</span>')}</div>` : ''}
    </header>
    <table>
      <thead><tr>
        <th class="no">#</th>
        <th class="nm">재료 <span>食材</span></th>
        <th class="q">기준량 <span>基准量</span></th>
        ${showScaled ? `<th class="q">${sv}인분 <span>${sv}人份</span></th>` : ''}
      </tr></thead>
      <tbody>${rows || `<tr><td colspan="4" class="empty">재료 없음 · 无食材</td></tr>`}</tbody>
    </table>
    ${(note || noteZh) ? `<div class="note">
      <div class="nt">조리 메모 <span>制作备注</span></div>
      ${note ? `<p class="ko">${nl2br(note)}</p>` : ''}
      ${noteZh ? `<p class="zh">${nl2br(noteZh)}</p>` : ''}
    </div>` : ''}
  </section>`;
}

const CSS = `
  @page { size: A4; margin: 14mm 12mm; }
  * { box-sizing: border-box; }
  body { margin: 0; color: #111;
         font-family: "Noto Sans KR","Malgun Gothic","Apple SD Gothic Neo",
                      "Noto Sans SC","PingFang SC","Microsoft YaHei",sans-serif; }
  .bar { position: sticky; top: 0; background: #18181B; color: #fff; padding: 10px 16px;
         display: flex; justify-content: space-between; align-items: center; font-size: 14px; }
  .bar button { background: #FF6F0F; color: #fff; border: 0; border-radius: 8px;
                padding: 8px 16px; font-size: 14px; font-weight: 700; cursor: pointer; }
  .recipe { padding: 18px 8px 8px; page-break-after: always; break-after: page; }
  .recipe:last-of-type { page-break-after: auto; break-after: auto; }
  header { border-bottom: 3px solid #111; padding-bottom: 10px; margin-bottom: 14px; }
  .badge { display: inline-block; font-size: 13px; font-weight: 700; color: #555;
           border: 1.5px solid #999; border-radius: 6px; padding: 2px 10px; margin-bottom: 8px; }
  h1 { font-size: 34px; margin: 0; line-height: 1.2; }
  h2 { font-size: 28px; margin: 4px 0 0; color: #1A56A0; font-weight: 700; line-height: 1.25; }
  .meta { margin-top: 8px; font-size: 15px; color: #444; }
  .meta .sep { color: #bbb; margin: 0 10px; }
  table { width: 100%; border-collapse: collapse; font-size: 18px; }
  th { text-align: left; font-size: 14px; color: #555; border-bottom: 2px solid #111; padding: 6px 8px; }
  th span { color: #1A56A0; font-weight: 600; margin-left: 4px; }
  td { border-bottom: 1px solid #ddd; padding: 9px 8px; vertical-align: middle; }
  tr { page-break-inside: avoid; break-inside: avoid; }
  .no { width: 36px; color: #888; text-align: center; }
  .nm .ko { font-weight: 700; }
  .nm .zh { font-size: 16px; color: #1A56A0; margin-top: 2px; }
  .py { font-size: 13px; color: #777; margin-top: 1px; letter-spacing: .01em; }
  .py.t { font-size: 17px; margin-top: 2px; }
  .q { text-align: right; white-space: nowrap; width: 120px; font-variant-numeric: tabular-nums; }
  th.q { text-align: right; }
  .q .u { font-size: 13px; color: #777; margin-left: 2px; }
  .q.strong { font-weight: 800; font-size: 20px; }
  .empty { text-align: center; color: #999; }
  .note { margin-top: 18px; border: 2px solid #111; border-radius: 10px; padding: 12px 14px;
          page-break-inside: avoid; break-inside: avoid; }
  .note .nt { font-size: 14px; font-weight: 700; color: #555; margin-bottom: 6px; }
  .note .nt span { color: #1A56A0; margin-left: 4px; }
  .note p { margin: 4px 0; font-size: 17px; line-height: 1.55; }
  .note p.zh { color: #1A56A0; }
  footer { font-size: 11px; color: #999; text-align: right; padding: 0 8px 12px; }
  @media print { .bar { display: none; } }
`;

/**
 * 레시피를 새 창에 띄워 인쇄한다.
 * 클릭 핸들러 안에서 동기로 불러야 팝업 차단에 안 걸린다.
 * @param {Array} recipes
 * @param {object} opts { zhMap, servings, title }
 * @returns {boolean} 창을 열었는지
 */
export function printRecipes(recipes, opts = {}) {
  const { zhMap = new Map(), servings = 1, title = '레시피 配方', lang = 'ko' } = opts;
  const list = (recipes || []).filter(r => r && r.name);
  if (list.length === 0) return false;

  const win = window.open('', '_blank');
  if (!win) return false;

  // V127: 병음 사전이 아직이면 창부터 열어 두고(팝업 차단 방지) 준비되면 채운다
  if (lang !== 'zh' && !isPinyinReady()) {
    win.document.open();
    win.document.write('<!doctype html><meta charset="utf-8"><p style="font:16px sans-serif;padding:24px">인쇄 준비 중… 准备打印…</p>');
    win.document.close();
    ensurePinyin().then(() => { if (!win.closed) writeRecipes(win, list, { zhMap, servings, title, lang }); });
    return true;
  }
  writeRecipes(win, list, { zhMap, servings, title, lang });
  return true;
}

function writeRecipes(win, list, { zhMap, servings, title, lang }) {
  const today = new Date();
  const ymd = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
  const body = list.map(r => recipeBlock(r, zhMap, servings, lang)).join('');

  win.document.open();
  win.document.write(`<!doctype html><html lang="ko"><head><meta charset="utf-8">
<meta name="viewport" content="width=device-width,initial-scale=1">
<title>${esc(title)}</title><style>${CSS}</style></head><body>
<div class="bar"><span>${esc(title)} · ${list.length}건</span>
<button onclick="window.print()">🖨 인쇄 · 打印</button></div>
${body}
<footer>셈핏 · 인쇄일 打印日期 ${ymd}</footer>
</body></html>`);
  win.document.close();
}

// ── V154: 레시피 + 단가표 한 번에 (고른 것만) ─────────────────────────────────
// 태블릿을 못 쓰는 직원용 종이. 레시피는 '레시피마다 한 장'(벽에 붙이기) 또는 '이어서'(책자·종이 절약).
// 단가표는 발주처별 표 — 구분·재료·규격·단위·(단가)·중량·메모 칸(손으로 적기).
const BUNDLE_CSS = `
  .flow .recipe { page-break-after: auto; break-after: auto; page-break-inside: avoid; break-inside: avoid;
                  border-bottom: 2px dashed #bbb; padding-bottom: 14px; margin-bottom: 6px; }
  .flow h1 { font-size: 26px; } .flow h2 { font-size: 21px; }
  .flow table { font-size: 16px; } .flow td { padding: 6px 8px; }
  .prices { padding: 18px 8px 8px; }
  .prices.after { page-break-before: always; break-before: page; }
  .prices h1 span { color: #1A56A0; font-size: 24px; margin-left: 8px; }
  .prices h3 { font-size: 19px; margin: 18px 0 6px; border-bottom: 2px solid #111; padding-bottom: 4px; }
  .prices h3 span { color: #1A56A0; margin-left: 6px; font-size: 16px; }
  table.pt { font-size: 15px; }
  table.pt th { font-size: 12px; white-space: nowrap; }
  table.pt td { padding: 6px 7px; }
  table.pt .zh { color: #1A56A0; font-size: 13px; }
  table.pt .cat { font-size: 12px; color: #555; white-space: nowrap; }
  table.pt .num { text-align: right; white-space: nowrap; font-variant-numeric: tabular-nums; }
  table.pt .loss { font-size: 11px; color: #B45309; }
  table.pt .memo { width: 18%; border-left: 1px dashed #bbb; }
  .toc { padding: 18px 8px; page-break-after: always; break-after: page; }
  .toc h1 span { color: #1A56A0; font-size: 24px; margin-left: 8px; }
  .toc ol { columns: 2; font-size: 15px; line-height: 1.7; }
  .toc .zh { color: #1A56A0; margin-left: 6px; font-size: 13px; }
`;
const CAT_LABEL = { 신선식품: '生鲜', 공산품: '干货', 소모품: '耗材', 반제품: '半成品' };

function priceBlock(prices, { withPrice, money, after }) {
  const groups = new Map();
  for (const p of prices) {
    const v = String(p.vendor || '').trim() || '발주처 미지정';
    if (!groups.has(v)) groups.set(v, []);
    groups.get(v).push(p);
  }
  const ORDER = { 신선식품: 1, 공산품: 2, 소모품: 3, 반제품: 4 };
  const body = [...groups.entries()].sort((a, b) => a[0].localeCompare(b[0], 'ko')).map(([v, list]) => {
    const vz = list.map(p => String(p.vendor_zh || '').trim()).find(z => z && !hasHangul(z)) || (v === '발주처 미지정' ? '未指定供应商' : '');
    const rows = [...list].sort((a, b) => (ORDER[a.category] || 9) - (ORDER[b.category] || 9) || String(a.name).localeCompare(String(b.name), 'ko'))
      .map((p, i) => {
        const nz = String(p.name_zh || '').trim();
        const spec = String(p.spec || '').trim();
        const sz = spec ? (specToZh(spec) || String(p.spec_zh || '').trim()) : '';
        const unit = String(p.unit || '').trim();
        const uz = unit ? (unitToZh(unit) || '') : '';
        const net = Number(p.net_g) || 0;
        const loss = lossG(p);
        const w = net > 0
          ? `${esc(net)}g${p.category === '공산품' ? ' NET' : ''}${loss ? `<div class="loss">− 로스 损耗 ${esc(loss)}g = ${esc(usableG(p))}g</div>` : ''}`
          : '';
        return `<tr>
          <td class="no">${i + 1}</td>
          <td class="cat">${esc(p.category || '')}${CAT_LABEL[p.category] ? `<br>${CAT_LABEL[p.category]}` : ''}</td>
          <td class="nm"><div class="ko">${esc(p.name)}</div>${nz && nz !== p.name ? `<div class="zh">${esc(nz)}</div>` : ''}</td>
          <td>${esc(spec)}${sz && sz !== spec ? `<div class="zh">${esc(sz)}</div>` : ''}</td>
          <td>${esc(unit)}${uz ? `<div class="zh">${esc(uz)}</div>` : ''}</td>
          ${withPrice ? `<td class="num">${p.price !== '' && p.price != null ? esc(money(p.price)) : ''}</td>` : ''}
          <td class="num">${w}</td>
          <td class="memo"></td>
        </tr>`;
      }).join('');
    return `<h3>🏪 ${esc(v)}${vz && vz !== v ? `<span>${esc(vz)}</span>` : ''} <small style="font-weight:500;color:#777">(${list.length})</small></h3>
      <table class="pt"><thead><tr>
        <th class="no">#</th><th>구분 <span>分类</span></th><th>재료 <span>食材</span></th><th>규격 <span>规格</span></th><th>단위 <span>单位</span></th>
        ${withPrice ? '<th class="num">단가 <span>单价</span></th>' : ''}<th class="num">중량 <span>重量</span></th><th class="memo">메모 <span>备注</span></th>
      </tr></thead><tbody>${rows}</tbody></table>`;
  }).join('');
  return `<section class="prices${after ? ' after' : ''}">
    <header><h1>단가표<span>单价表</span></h1><div class="meta">${prices.length}품목 · ${prices.length}项</div></header>
    ${body}
  </section>`;
}

/**
 * 레시피 + 단가표를 한 창에 (클릭 안에서 동기로 불러야 팝업 차단에 안 걸린다)
 * @param {object} o { recipes, prices, zhMap, lang, layout: 'page'|'flow', withPrice, money, servings, title }
 * @returns {boolean}
 */
export function printBundle(o = {}) {
  const recipes = (o.recipes || []).filter(r => r && r.name);
  const prices = (o.prices || []).filter(p => p && p.name);
  if (!recipes.length && !prices.length) return false;
  const win = window.open('', '_blank');
  if (!win) return false;
  const write = () => writeBundle(win, { ...o, recipes, prices });
  if (recipes.length && o.lang !== 'zh' && !isPinyinReady()) {
    win.document.open();
    win.document.write('<!doctype html><meta charset="utf-8"><p style="font:16px sans-serif;padding:24px">인쇄 준비 중… 准备打印…</p>');
    win.document.close();
    ensurePinyin().then(() => { if (!win.closed) write(); });
    return true;
  }
  write();
  return true;
}

function writeBundle(win, { recipes, prices, zhMap = new Map(), lang = 'ko', layout = 'page', withPrice = true,
                            money = (n) => String(n), servings = 1, title = '레시피 · 단가표 配方 · 单价表' }) {
  const today = new Date();
  const ymd = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
  const toc = recipes.length > 1 && layout === 'page'
    ? `<section class="toc"><h1>목차<span>目录</span></h1><ol>${recipes.map(r => `<li>${esc(r.name)}${r.name_zh ? `<span class="zh">${esc(r.name_zh)}</span>` : ''}</li>`).join('')}</ol></section>`
    : '';
  const rec = recipes.map(r => recipeBlock(r, zhMap, servings, lang)).join('');
  const pr = prices.length ? priceBlock(prices, { withPrice, money, after: recipes.length > 0 }) : '';
  const count = [recipes.length ? `레시피 ${recipes.length} · 配方 ${recipes.length}` : '', prices.length ? `단가표 ${prices.length} · 单价表 ${prices.length}` : ''].filter(Boolean).join(' / ');
  win.document.open();
  win.document.write(`<!doctype html><html lang="ko"><head><meta charset="utf-8">
<meta name="viewport" content="width=device-width,initial-scale=1">
<title>${esc(title)}</title><style>${CSS}${BUNDLE_CSS}</style></head><body class="${layout === 'flow' ? 'flow' : 'page'}">
<div class="bar"><span>${esc(title)} · ${esc(count)}</span>
<button onclick="window.print()">🖨 인쇄 · 打印</button></div>
${toc}${rec}${pr}
<footer>셈핏 · 인쇄일 打印日期 ${ymd}</footer>
</body></html>`);
  win.document.close();
}

export default printRecipes;
